// pattern: Imperative Shell

import { html } from 'htm/preact'
import { type FunctionComponent } from 'preact'
import { useLayoutEffect, useRef } from 'preact/hooks'

// Match the @substrate-system/details-summary motion vocabulary so the
// section grow/collapse reads as the same gesture as the inner disclosure.
const DEFAULT_DURATION = 300  // ms
const EASING = 'ease-out'

export interface MethodSectionTransitionProps {
    method:string;        // data-method value; identifies the section
    enter:boolean;        // play grow-in on mount (false = instant)
    exit:boolean;         // outgoing snapshot: collapse then unmount
    onExited:() => void;  // called once when the exit collapse finishes
    duration?:number;     // ms, default 300 (matches details-summary)
    children:unknown;     // methodSection(...) output, or its snapshot
}

function prefersReducedMotion ():boolean {
    return typeof matchMedia === 'function' &&
        matchMedia('(prefers-reduced-motion: reduce)').matches
}

// Owns the `.method-section[data-method]` element (so the focus helper
// `methodScope` keeps matching) and plays a height animation on mount. Resting
// state is the element's natural flow height — no inline height is persisted,
// so the section stays responsive after the animation.
export const MethodSectionTransition:FunctionComponent<
    MethodSectionTransitionProps
> = function (props) {
    const { method, enter, exit, children } = props
    const duration = props.duration ?? DEFAULT_DURATION
    const elementRef = useRef<HTMLDivElement>(null)
    // Hold the latest onExited so the mount effect can stay [] (run once)
    // without going stale.
    const onExitedRef = useRef(props.onExited)
    onExitedRef.current = props.onExited

    useLayoutEffect(() => {
        const el = elementRef.current
        if (!el) return
        const reduced = prefersReducedMotion()

        if (exit) {
            if (reduced) {
                onExitedRef.current()
                return
            }
            const start = el.offsetHeight
            el.style.overflow = 'hidden'
            const animation = el.animate(
                { height: [`${start}px`, '0px'], opacity: [1, 0] },
                { duration, easing: EASING }
            )
            // Only finish (not cancel) prunes the exiting set. A cancel here
            // means the parent already removed this snapshot (route switch or
            // a re-check), so onExited would be redundant.
            animation.onfinish = () => onExitedRef.current()
            return () => animation.cancel()
        }

        if (enter && !reduced) {
            const natural = el.offsetHeight
            el.style.overflow = 'hidden'
            const animation = el.animate(
                { height: ['0px', `${natural}px`] },
                { duration, easing: EASING }
            )
            animation.onfinish = () => { el.style.overflow = '' }
            return () => animation.cancel()
        }

        // enter=false, or reduced-motion enter: mount at natural height with
        // no animation. Nothing to do.
    }, [])

    return html`<div
        class="method-section"
        data-method=${method}
        data-exiting=${exit ? '' : undefined}
        inert=${exit}
        aria-hidden=${exit ? 'true' : undefined}
        ref=${elementRef}
    >${children}</div>`
}
